import { getFulcrumPhotoAsBlob, FULCRUM_BASE_URLS, FulcrumRegion } from "./fulcrumLibService";
import { constructMediaUrl } from "./mediaUtils";
import { getDataSourceType } from "./dataSourceAdapters";
import { constructThumbnailUrl } from "./temporaryUrlUtils";
import { isEnvCdnUrl } from "./cdnUtils";
import { assetDisplayUrl, isApiMedia, isEmbedAsset } from "./mediaAsset";
import { devLog } from "./devLog";

// ============================================================================
// Generalized Authentication-Based Media Adapter Interfaces
// ============================================================================

export type MediaResourceType = "thumbnail" | "preview" | "full";

export interface MediaResourceOptions {
  size?: { width: number; height: number };
  quality?: number;
  format?: "jpg" | "png" | "webp";
}

export interface MediaResourceResult {
  url: string;
  authMethod: "headers" | "cookies" | "direct" | "api";
  requiresBlobConversion: boolean;
  isTested: boolean;
  fallbackAvailable: boolean;
}

export interface AuthenticatedApiConfig {
  name: string;
  apiKey: string;
  baseUrl: string;
  headers: Record<string, string>;
  regionDetector?: (item: any) => string;
  photoUrlBuilder: (photoId: string, region: string, thumbnail: boolean) => string;
  photoBlobFetcher: (photoId: string, userProfile: any, region: string, thumbnail: boolean) => Promise<string>;
}

/**
 * Handle cookie-authenticated resources (our CDN). API media carries every URL
 * it needs, so nothing is fetched, probed or cached.
 */
async function getCookieAuthenticatedResource(
  item: any,
  resourceType: MediaResourceType,
  options: MediaResourceOptions = {}
): Promise<MediaResourceResult> {
  const result: MediaResourceResult = {
    url: "",
    authMethod: "cookies",
    requiresBlobConversion: false,
    isTested: false,
    fallbackAvailable: true
  };

  devLog(`[CookieAuthenticatedResource] Processing ${resourceType} for item:`, item.id);

  // API media carries everything needed, so there is nothing to fetch, guess or cache:
  // its thumbnail (null when there is none), and what to show (`display_url`, or the
  // original file; an embed's `display_url` is its iframe src).
  if (isApiMedia(item)) {
    const url = resourceType === "thumbnail" ? item.thumbnail || "" : assetDisplayUrl(item);
    if (!url) {
      result.fallbackAvailable = false;
      return result;
    }
    const isEmbed = isEmbedAsset(item) && resourceType !== "thumbnail";
    result.url = isEmbed ? url : constructMediaUrl(url);
    result.authMethod = isEmbed || !isEnvCdnUrl(result.url) ? "direct" : "cookies";
    result.isTested = isEmbed;
    return result;
  }

  // Anything else (not from our media API, e.g. an item still being prepared for
  // upload): its own thumbnail, URL or pasted embed.
  if (resourceType === "thumbnail") {
    const thumbnailUrl = item.entity_thumbnail || item.thumbnail || item.primary_image?.thumbnail || constructThumbnailUrl(item);
    if (typeof thumbnailUrl === "string" && thumbnailUrl) {
      result.url = constructMediaUrl(thumbnailUrl);
      result.authMethod = isEnvCdnUrl(result.url) ? "cookies" : "direct";
      return result;
    }
  } else if (item.embed_url) {
    result.url = item.embed_url;
    result.authMethod = "direct";
    result.isTested = true;
    return result;
  } else {
    const sourceUrl = item.url || item.primary_image?.url;
    if (sourceUrl) {
      result.url = constructMediaUrl(sourceUrl);
      result.authMethod = isEnvCdnUrl(result.url) ? "cookies" : "direct";
      return result;
    }
  }

  console.warn(`[CookieAuthenticatedResource] No ${resourceType} URL found for item:`, item.id);
  result.fallbackAvailable = false;
  return result;
}

// ============================================================================
// Header-Authenticated Resources (Generic for any API requiring headers)
// ============================================================================

export const getHeaderAuthenticatedResource = async (
  item: any,
  resourceType: MediaResourceType,
  apiConfig: AuthenticatedApiConfig,
  userProfile: any,
  options: MediaResourceOptions = {}
): Promise<MediaResourceResult> => {
  const result: MediaResourceResult = {
    url: "",
    authMethod: "api",
    requiresBlobConversion: false,
    isTested: false,
    fallbackAvailable: false
  };

  // Check for photo data (generic approach) - avoid "thumbnail" string from bulk processing failures
  const photoId = item.fulcrum_photo?.photo_id || (item.primary_image?.id !== "thumbnail" ? item.primary_image?.id : null) || item.id;
  const formId = item.fulcrum_photo?.form_id || item.fulcrum_data?.form_id || item.form_id;

  if (photoId && (item.fulcrum_photo || item.primary_image)) {
    // Detect region using provided detector or default
    const region = apiConfig.regionDetector ? apiConfig.regionDetector(item) : "AU";

    try {
      switch (resourceType) {
        case "thumbnail":
          result.url = await apiConfig.photoBlobFetcher(photoId, userProfile, region.toUpperCase(), true);
          result.authMethod = "headers";
          result.requiresBlobConversion = true;
          result.fallbackAvailable = true;
          return result;

        case "preview":
          // For previews, use full size photo
          result.url = await apiConfig.photoBlobFetcher(photoId, userProfile, region.toUpperCase(), false);
          result.authMethod = "headers";
          result.requiresBlobConversion = true;
          result.fallbackAvailable = true;
          return result;

        case "full":
          // Full size photo
          result.url = await apiConfig.photoBlobFetcher(photoId, userProfile, region.toUpperCase(), false);
          result.authMethod = "headers";
          result.requiresBlobConversion = true;
          result.fallbackAvailable = true;
          return result;
      }
    } catch (error) {
      console.error(`[${apiConfig.name}Resource] ❌ Error fetching authenticated photo:`, error);

      // Check if this is a 403 Forbidden error (expired signature, permissions, etc.)
      const errorMessage = error instanceof Error ? error.message : String(error);
      const is403Error =
        errorMessage.includes("403") || errorMessage.includes("Forbidden") || errorMessage.toLowerCase().includes("forbidden");

      if (is403Error) {
        console.warn(`[${apiConfig.name}Resource] ⚠️ 403 Forbidden - trying original URL as fallback`);

        // Try to fall back to the original URL from the item
        const originalUrl = resourceType === "thumbnail" ? item.thumbnail : item.url;
        const fallbackUrl = originalUrl || item.url || item.thumbnail;

        if (fallbackUrl) {
          result.url = fallbackUrl;
          result.authMethod = "direct"; // Try direct access without authentication
          result.requiresBlobConversion = false;
          result.fallbackAvailable = true;
          result.isTested = true;
          return result;
        } else {
          console.warn(`[${apiConfig.name}Resource] ❌ No fallback URL available - all Fulcrum URLs require authentication`);
        }
      }

      // For other authenticated photo failures, return error result
      // (Fulcrum photos don't exist in Django and require private API keys)
      console.error(`[${apiConfig.name}Resource] ❌ Authentication failed for photo ${photoId}:`, error);

      // If this was a full-size request that failed, try falling back to thumbnail
      if (resourceType === "full" && item.thumbnail) {
        result.url = item.thumbnail;
        result.authMethod = "direct";
        result.requiresBlobConversion = false;
        result.fallbackAvailable = true;
        return result;
      }

      result.url = "";
      result.authMethod = "headers";
      result.requiresBlobConversion = true;
      result.fallbackAvailable = false;
      result.isTested = true;
      console.warn(`[${apiConfig.name}Resource] ⚠️ Authentication failed - photo requires private API key`);
      return result;
    }
  }

  // Only fall back to cookie-authenticated handler for non-photo items
  // (items that don't have photo IDs but might have regular URLs)
  return getCookieAuthenticatedResource(item, resourceType, options);
};

// ============================================================================
// Pre-configured API Configs (for convenience)
// ============================================================================

/**
 * Creates Fulcrum API config with API key from user profile only
 * @param userProfile User profile containing API key and region
 */
const createFulcrumApiConfig = (userProfile: any): AuthenticatedApiConfig => {
  // Get API key from user profile only - no environment variable fallback
  const apiKey = userProfile?.fulcrum_api_key || "";
  // Get region from user profile, default to AU if not set
  const region: FulcrumRegion = userProfile?.fulcrum_region || "AU";

  if (!apiKey) {
    console.warn("No Fulcrum API key available in user profile for media adapter");
  }

  // Use shared FULCRUM_BASE_URLS constant (DRY)
  const baseUrl = FULCRUM_BASE_URLS[region] || FULCRUM_BASE_URLS.AU;

  return {
    name: "Fulcrum",
    apiKey: apiKey,
    baseUrl: baseUrl,
    headers: {
      "X-ApiToken": apiKey,
      "Content-Type": "application/json"
    },
    regionDetector: () => region, // Use region from user profile instead of guessing
    photoUrlBuilder: (photoId: string, regionParam: string, thumbnail: boolean) => {
      // Use the region passed in, or fall back to user profile region
      const effectiveRegion = (regionParam || region) as FulcrumRegion;
      const photoBaseUrl = FULCRUM_BASE_URLS[effectiveRegion] || FULCRUM_BASE_URLS.AU;
      const suffix = thumbnail ? "/thumbnail.jpg" : ".jpg";
      return `${photoBaseUrl}/photos/${photoId}${suffix}`;
    },
    photoBlobFetcher: (photoId: string, userProfile: any, regionParam: string, thumbnail: boolean) =>
      getFulcrumPhotoAsBlob(photoId, userProfile, thumbnail)
  };
};

// ============================================================================
// Main Resource Router (Authentication-method based)
// ============================================================================

export const getMediaResource = async (
  item: any,
  resourceType: MediaResourceType,
  userProfile: any,
  options: MediaResourceOptions = {}
): Promise<MediaResourceResult> => {
  const dataSourceType = getDataSourceType(item);

  // Route based on authentication requirements, not vendor
  switch (dataSourceType) {
    case "fulcrum":
      // Use header-authenticated handler with secure Fulcrum config
      const fulcrumConfig = createFulcrumApiConfig(userProfile);
      return getHeaderAuthenticatedResource(item, resourceType, fulcrumConfig, userProfile, options);

    default:
      // Use cookie-authenticated handler (CDN + Django)
      return getCookieAuthenticatedResource(item, resourceType, options);
  }
};

// ============================================================================
// Convenience Methods (for backwards compatibility)
// ============================================================================

export const getMediaThumbnail = async (item: any, userProfile: any, options: MediaResourceOptions = {}): Promise<MediaResourceResult> => {
  return getMediaResource(item, "thumbnail", userProfile, options);
};

export const getMediaFull = async (item: any, userProfile: any, options: MediaResourceOptions = {}): Promise<MediaResourceResult> => {
  return getMediaResource(item, "full", userProfile, options);
};

export const getMediaThumbnailSync = (item: any): string => {
  if (!item) return "";

  const dataSourceType = getDataSourceType(item);

  // For sync operations, we can't do blob conversion, so fall back to URLs
  if (dataSourceType === "fulcrum") {
    console.warn("[MediaResourceRouter] ⚠️ Sync thumbnail requested for authentication-required item, may fail");
  }

  // Use existing sync logic
  const thumbnailUrl = constructThumbnailUrl(item);
  if (thumbnailUrl) {
    return thumbnailUrl;
  }

  return "";
};
